import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Dialog,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppNavigationCatalog } from '../../hooks/useAppNavigationCatalog';
import {
  useSystemFinderSearch,
  type FinderPatientOption,
  type SystemFinderFlatRow,
} from '../../hooks/useSystemFinderSearch';
import inboxService from '../../services/inboxService';
import PatientDetailPanel from '../PatientDetailPanel';
import PatientFormDialog from '../PatientFormDialog';
import { SIDEBAR_THEME } from './sidebarTheme';
import { globalSystemFinderBarSx, SYSTEM_FINDER_PLACEHOLDER } from './systemFinderConstants';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { AGENDA_THEME } from '../../pages/agendaTheme';

/** Busca global inline — resultados abaixo do próprio campo (sem modal). */
const GlobalSystemFinder: React.FC = () => {
  const navigate = useNavigate();
  const { isMobileMd: isMobile } = useDeviceLayout();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [finderInboxEntries, setFinderInboxEntries] = useState<Array<{ text: string; path: string }>>([]);
  const navigationCatalogEntries = useAppNavigationCatalog(finderInboxEntries);
  const {
    query,
    setQuery,
    patientLoading,
    patients,
    flatRows,
    activeIndex,
    setActiveIndex,
    reset,
  } = useSystemFinderSearch(navigationCatalogEntries, open);

  const [standalonePatientPanel, setStandalonePatientPanel] = useState<{
    patientId: string;
    initialPatient: FinderPatientOption;
  } | null>(null);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  const [patientForForm, setPatientForForm] = useState<any | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const menuData = await inboxService.getMenuInboxes();
        const all = (menuData.all || []).filter((i) => i.purpose !== 'platform_support');
        if (!cancelled) {
          setFinderInboxEntries(all.map((i) => ({ text: i.text, path: i.path })));
        }
      } catch {
        if (!cancelled) setFinderInboxEntries([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const closePanel = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const focusField = useCallback(() => {
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (isMobile) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      e.preventDefault();
      focusField();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusField, isMobile]);

  const activateRow = useCallback((row: SystemFinderFlatRow) => {
    if (row.kind === 'patient') {
      setStandalonePatientPanel({ patientId: row.patient.id, initialPatient: row.patient });
      closePanel();
      return;
    }
    navigate(row.entry.path);
    closePanel();
  }, [closePanel, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flatRows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatRows[activeIndex]) {
      e.preventDefault();
      activateRow(flatRows[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
      inputRef.current?.blur();
    }
  };

  if (isMobile) return null;

  const showDropdown = open;

  return (
    <>
      <Box
        component="header"
        aria-label="Busca global do sistema"
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'stretch',
          alignItems: 'stretch',
          px: 0,
          py: 0,
          minHeight: 32,
          bgcolor: SIDEBAR_THEME.paper,
          borderBottom: `1px solid ${SIDEBAR_THEME.borderSoft}`,
          zIndex: (theme) => theme.zIndex.appBar,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <ClickAwayListener onClickAway={closePanel}>
          <Box
            ref={anchorRef}
            component="form"
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            sx={{ width: '100%', minWidth: 0, flex: 1 }}
          >
            <TextField
              inputRef={inputRef}
              size="small"
              fullWidth
              name="klinvia-global-system-finder"
              autoComplete="off"
              placeholder={SYSTEM_FINDER_PLACEHOLDER}
              aria-label={SYSTEM_FINDER_PLACEHOLDER}
              aria-expanded={showDropdown}
              aria-controls="global-system-finder-listbox"
              role="combobox"
              value={query}
              inputProps={{
                autoComplete: 'off',
                'data-1p-ignore': true,
                'data-lpignore': 'true',
                'data-form-type': 'other',
              }}
              onChange={(e) => {
                setQuery(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: SIDEBAR_THEME.slate }} />
                  </InputAdornment>
                ),
              }}
              sx={globalSystemFinderBarSx}
            />

            <Popper
              open={showDropdown}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              disablePortal
              modifiers={[
                { name: 'offset', options: { offset: [0, 4] } },
                {
                  name: 'sameWidth',
                  enabled: true,
                  phase: 'beforeWrite',
                  requires: ['computeStyles'],
                  fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                  },
                },
              ]}
              sx={{ zIndex: (theme) => theme.zIndex.modal }}
            >
              <Paper
                id="global-system-finder-listbox"
                role="listbox"
                elevation={8}
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: `1px solid ${SIDEBAR_THEME.borderSoft}`,
                  maxHeight: 360,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="caption" sx={{ px: 1.5, py: 0.75, color: '#64748b' }}>
                  ↑↓ navegar · Enter abrir · Esc fechar
                </Typography>
                <Divider />
                <List dense sx={{ overflowY: 'auto', py: 0.5, flex: 1 }}>
                  {patientLoading && patients.length === 0 && !query.trim() ? (
                    <Typography variant="body2" sx={{ px: 2, py: 1.5, color: '#64748b' }}>
                      Carregando…
                    </Typography>
                  ) : null}
                  {flatRows.length === 0 && !patientLoading ? (
                    <Typography variant="body2" sx={{ px: 2, py: 1.5, color: '#64748b' }}>
                      Nenhum paciente ou função encontrada.
                    </Typography>
                  ) : null}
                  {patients.length > 0 ? (
                    <Typography variant="overline" sx={{ px: 2, color: '#94a3b8', display: 'block' }}>
                      Pacientes
                    </Typography>
                  ) : null}
                  {flatRows.map((row, index) => {
                    if (row.kind === 'patient') {
                      const selected = index === activeIndex;
                      return (
                        <ListItemButton
                          key={row.key}
                          role="option"
                          aria-selected={selected}
                          selected={selected}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => activateRow(row)}
                        >
                          <PersonIcon sx={{ fontSize: 18, color: AGENDA_THEME.tealDark, mr: 1.25 }} />
                          <ListItemText
                            primary={row.patient.full_name}
                            secondary={row.patient.cpf ? `CPF ${row.patient.cpf}` : 'Abrir painel do paciente'}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItemButton>
                      );
                    }
                    const selected = index === activeIndex;
                    return (
                      <ListItemButton
                        key={row.key}
                        role="option"
                        aria-selected={selected}
                        selected={selected}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => activateRow(row)}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16, color: '#64748b', mr: 1.25 }} />
                        <ListItemText
                          primary={row.entry.label}
                          secondary={row.entry.group}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      </Box>

      <Dialog
        open={Boolean(standalonePatientPanel)}
        onClose={() => setStandalonePatientPanel(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {standalonePatientPanel ? (
          <PatientDetailPanel
            variant="agenda-embedded"
            patientId={standalonePatientPanel.patientId}
            initialPatient={standalonePatientPanel.initialPatient}
            onClose={() => setStandalonePatientPanel(null)}
            onEdit={(p) => {
              setStandalonePatientPanel(null);
              setPatientForForm(p);
              setPatientFormOpen(true);
            }}
            onPatientUpdated={(updated) => {
              setStandalonePatientPanel((prev) => (
                prev ? { ...prev, initialPatient: { ...prev.initialPatient, ...updated, id: String(updated.id) } } : prev
              ));
            }}
          />
        ) : null}
      </Dialog>

      <PatientFormDialog
        open={patientFormOpen}
        onClose={() => setPatientFormOpen(false)}
        patientToEdit={patientForForm}
        onSaved={() => setPatientFormOpen(false)}
        onError={() => {}}
      />
    </>
  );
};

export default GlobalSystemFinder;
